/**
 * Solver for the unsubscribe page's human check: an ALTCHA proof-of-work
 * (https://altcha.org, MIT protocol). marvin-core issues a signed puzzle,
 * `challenge = sha256(salt + number)`; we find `number` (about a second of
 * work, invisible to a person) and send the answer with the mutation, which
 * the server verifies before acting. Mail scanners never get this far: the
 * page only acts on a button press.
 *
 * Uses the browser's built-in crypto.subtle - no dependency.
 */

const BATCH = 500;

export async function sha256Hex(
  text,
  subtle = typeof window !== 'undefined' &&
    window.crypto &&
    window.crypto.subtle
) {
  if (!subtle) {
    throw new Error('crypto.subtle is not available');
  }
  const bytes = await subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(bytes), b =>
    b.toString(16).padStart(2, '0')
  ).join('');
}

/**
 * The secret number for this challenge, or null if none is found within
 * maxnumber (or the time budget). Hashes in parallel batches: one await per
 * candidate would make the search several times slower.
 */
export async function solveChallenge(
  challenge,
  { digest = sha256Hex, maxMs = 20000, now = () => Date.now() } = {}
) {
  const { salt, maxnumber } = challenge;
  const target = String(challenge.challenge).toLowerCase();
  const started = now();
  for (let from = 0; from <= maxnumber; from += BATCH) {
    const to = Math.min(from + BATCH - 1, maxnumber);
    const candidates = [];
    for (let n = from; n <= to; n++) candidates.push(n);
    const hashes = await Promise.all(candidates.map(n => digest(salt + n)));
    const hit = hashes.indexOf(target);
    if (hit >= 0) return candidates[hit];
    if (now() - started > maxMs) return null;
  }
  return null;
}

/** The payload marvin-core expects: base64 of {algorithm, challenge, number, salt, signature}. */
export function encodePayload(challenge, number) {
  return btoa(
    JSON.stringify({
      algorithm: challenge.algorithm,
      challenge: challenge.challenge,
      number,
      salt: challenge.salt,
      signature: challenge.signature
    })
  );
}
